import PropTypes from 'prop-types';
import ReactTable from 'react-table-6';
import Radio from '@mui/material/Radio';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';
import Tabs from '@material-ui/core/Tabs';
import Slider from '@mui/material/Slider';
import { useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import AppBar from '@material-ui/core/AppBar';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import React, { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Typography from '@material-ui/core/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { Select, MenuItem, FormControl } from '@mui/material';

import constants from '../../../constants';
import { updateAgentVoice } from '../../../store/Agent'
import { fontSize } from '../../../../node_modules/@mui/system/index';

const TopSettings = ({ agentData, setAgentData, setUpdated }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { voices, allModels, ttsProviders } = useSelector(state => state['Agent']);

  const [file, setFile] = useState(null);
  const [searchVoice, setSearchVoice] = useState('');
  const [audioPlayer, setAudioPlayer] = useState(null);
  const [valueLLMRange, setValueLLMRange] = useState(30);
  const [selectedAccent, setSelectedAccent] = useState('');
  const [filteredVoices, setFilteredVoices] = useState([]);
  const [selectedGender, setSelectedGender] = useState('');
  const [voiceProvider, setVoiceProvider] = useState('Cartesia');
  const [valueCustomVoiceTab, setValueCustomVoiceTab] = useState(0);
  const [currentPlayingVoice, setCurrentPlayingVoice] = useState(null);
  const [dynamicFilters, setDynamicFilters] = useState({ genders: [], accents: [] });
  const [openState, setOpenState] = useState({ gender: false, accent: false, isVoiceSettings: false, isLLMSettings: false, isModelChange: false, isVoiceChange: false });

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'audio/*, video/*',
    maxSize: 10 * 1024 * 1024, // 10 MB limit
    onDrop: (acceptedFiles) => setFile(acceptedFiles[0])
  });

  // Initialize the filtered voices when component mounts
  useEffect(() => { filterVoicesFunc() }, [selectedGender, selectedAccent, searchVoice, voiceProvider, voices]);

  // Initialize LLM temperature from agentData
  useEffect(() => {
    if (agentData?.llmTemperature !== undefined) {
      setValueLLMRange(agentData.llmTemperature);
    }
  }, [agentData?.llmTemperature]);

  // Filter and prepare data for the table
  const filterVoicesFunc = () => {
    let voicesArray = voices;
    voicesArray = voices.filter(voice => voice['voiceProvider'] === voiceProvider);
    if (selectedGender && selectedGender !== 'All Gender') voicesArray = voicesArray.filter(voice => voice['gender'] === selectedGender);
    if (selectedAccent && selectedAccent !== 'All Accent') voicesArray = voicesArray.filter(voice => voice['accent'] === selectedAccent);
    if (searchVoice) voicesArray = voicesArray.filter(voice => voice['voiceName']?.toLowerCase().includes(searchVoice?.toLowerCase()) || voice['accent']?.toLowerCase().includes(searchVoice?.toLowerCase()) || voice['gender']?.toLowerCase().includes(searchVoice?.toLowerCase()));
    setFilteredVoices(voicesArray);

    if (voices.length > 0) {
      const genders = ['All Gender', ...new Set(voices.map(v => v.gender))];
      const accents = ['All Accent', ...new Set(voices.map(v => v.accent))];
      setDynamicFilters({ genders, accents });
    }
  }

  const handleSliderChange = (e, newValue) => setValueLLMRange(newValue);
  const handleTabsSetCustomVoice = (e, newValueCustomVoiceTab) => setValueCustomVoiceTab(newValueCustomVoiceTab);
  const handleBlur = () => { if (valueLLMRange < 0) setValueLLMRange(0); else if (valueLLMRange > 100) setValueLLMRange(100) };
  const handleInputChange = (e) => setValueLLMRange(e['target']['valueLLMRange'] === '' ? 0 : Number(e['target']['valueLLMRange']));

  const playVoiceSample = (demoUrl, voiceName) => {
    if (audioPlayer) {
      if (!audioPlayer['paused']) {
        audioPlayer.pause();
      }
      audioPlayer['currentTime'] = 0;
    }

    if (currentPlayingVoice === voiceName) {
      setCurrentPlayingVoice(null);
      return;
    }

    const audio = new Audio(demoUrl);
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch(error => {
        // Play was interrupted, ignore the error
        console.log("Play interrupted:", error);
      });
    }
    setAudioPlayer(audio);
    setCurrentPlayingVoice(voiceName);
    audio.onended = () => setCurrentPlayingVoice(null);
  };

  const handleSelectGender = (value) => { setOpenState({ ...openState, gender: false }); setSelectedGender(value); };
  const handleSelectAccent = (value) => { setOpenState({ ...openState, accent: false }); setSelectedAccent(value); };

  const handleSaveLLMSettings = () => {
    setAgentData({ ...agentData, llmTemperature: valueLLMRange });
    setUpdated(true);
    setOpenState({ ...openState, isLLMSettings: false });
  };

  const handleCancelLLMSettings = () => {
    setValueLLMRange(agentData?.llmTemperature || 30);
    setOpenState({ ...openState, isLLMSettings: false });
  };

  const llmSettings = () => {
    return (
      <div className='dropdown-settings'>
        <div className='dropdown-settings-inner'>
          <div className='group-form'>
            <h4>LLM Temperature</h4>
            <p>Lower value yields better function call results.</p>
            <div className='slider-area'>
              <Slider
                onChange={handleSliderChange}
                aria-labelledby='input-slider'
                value={typeof valueLLMRange === 'number' ? valueLLMRange : 0}
              />
              <input
                size='small'
                onBlur={handleBlur}
                value={valueLLMRange}
                onChange={handleInputChange}
                inputProps={{ step: 10, min: 0, max: 100, type: 'number', 'aria-labelledby': 'input-slider' }}
              />
            </div>
            <div className='text-box'>
              <h4>Structured Output</h4>
              <p>Always generate responses that adhere to your supplied JSON Schema. This will make functions longer to save or update.</p>
            </div>
          </div>
          <div className='btn-group text-right'>
            <button className='btn-style-border' onClick={handleCancelLLMSettings}>Cancel</button>
            <button className='btn-style-new' onClick={handleSaveLLMSettings}>Save</button>
          </div>
        </div>
      </div>
    )
  }

  const voiceSettings = () => {
    return (
      <div className='dropdown-settings'>
        <div className='dropdown-settings-inner'>
          <div className='group-form'>
            <h4>Voice Model</h4>
            <div className='radio-box-area'>
              <label value='female' className='label-radio'>
                <Radio for='female' />
                <div className='right-text'>
                  <h4>Auto(Elevenlabs Turbo V2)</h4>
                  <p>English only, fast, high quality</p>
                </div>
              </label>
            </div>
            <div className='radio-box-area'>
              <label value='female' className='label-radio'>
                <Radio for='female' />
                <div className='right-text'>
                  <h4>Elevenlabs Turbo V2</h4>
                  <p>English only, fast, high quality</p>
                </div>
              </label>
            </div>
            <div className='radio-box-area'>
              <label value='female' className='label-radio'>
                <Radio for='female' />
                <div className='right-text'>
                  <h4>Elevenlabs Flash V2</h4>
                  <p>English only, fastest, medium quality</p>
                </div>
              </label>
            </div>
            <div className='radio-box-area'>
              <label value='female' className='label-radio'>
                <Radio for='female' />
                <div className='right-text'>
                  <h4>Elevenlabs Turbo V2.5</h4>
                  <p>Multilingual, fast, high quality</p>
                </div>
              </label>
            </div>
            <div className='radio-box-area'>
              <label value='female' className='label-radio'>
                <Radio for='female' />
                <div className='right-text'>
                  <h4>Elevenlabs Flash V2.5</h4>
                  <p>Multilingual, fastest, medium quality</p>
                </div>
              </label>
            </div>
            <div className='radio-box-area'>
              <label value='female' className='label-radio'>
                <Radio for='female' />
                <div className='right-text'>
                  <h4>Elevenlabs Multilingual v2</h4>
                  <p>Multilingual, slow, highest quality</p>
                </div>
              </label>
            </div>
            <h4>Voice Speed</h4>
            <div className='slider-area'>
              <Slider
                onChange={handleSliderChange}
                aria-labelledby='input-slider'
                value={typeof valueLLMRange === 'number' ? valueLLMRange : 0}
              />
              <input
                size='small'
                onBlur={handleBlur}
                value={valueLLMRange}
                onChange={handleInputChange}
                inputProps={{ step: 10, min: 0, max: 100, type: 'number', 'aria-labelledby': 'input-slider' }}
              />
            </div>
            <h4>Voice Temperature</h4>
            <div className='slider-area'>
              <Slider
                onChange={handleSliderChange}
                aria-labelledby='input-slider'
                value={typeof valueLLMRange === 'number' ? valueLLMRange : 0}
              />
              <input
                size='small'
                onBlur={handleBlur}
                value={valueLLMRange}
                onChange={handleInputChange}
                inputProps={{ step: 10, min: 0, max: 100, type: 'number', 'aria-labelledby': 'input-slider' }}
              />
            </div>

            <h4>Voice Volume</h4>
            <div className='slider-area'>
              <Slider
                onChange={handleSliderChange}
                aria-labelledby='input-slider'
                value={typeof valueLLMRange === 'number' ? valueLLMRange : 0}
              />
              <input
                size='small'
                onBlur={handleBlur}
                value={valueLLMRange}
                onChange={handleInputChange}
                inputProps={{ step: 10, min: 0, max: 100, type: 'number', 'aria-labelledby': 'input-slider' }}
              />
            </div>
          </div>
          <div className='btn-group text-right'>
            <button className='btn-style-border'>Cancel</button>
            <button className='btn-style-new'>Save</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className='owner-detail-voice'>
        <div className='img-box'>
          <img src={agentData?.['voiceId']?.['voiceImg']} alt='' />
        </div>
        <div className='text-box'>
          <h4>{agentData?.['voiceId']?.['voiceName']}</h4>
          <p>Give this agent a short description...</p>
        </div>
      </div>
      <div className='dropdown-header style-two'>
        {/* ========== Voice Selection Dropdown ========== */}
        <div className='gpt-dropdown style-three'>
          <button className='agent-select' onClick={() => setOpenState({ ...openState, isVoiceChange: true })}>
            {/* <i className='img-area'>
              <img src={agentData?.['voiceId']?.['voiceImg']} alt='' />
            </i> */}
            {agentData?.['voiceId']?.['voiceName']}
            <i className='arrow'>
              <svg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M5.59126 5.35841L9.1246 1.82507C9.2027 1.74761 9.2647 1.65544 9.307 1.55389C9.34931 1.45234 9.37109 1.34342 9.37109 1.23341C9.37109 1.1234 9.34931 1.01448 9.307 0.912927C9.2647 0.811378 9.2027 0.71921 9.1246 0.641741C8.96846 0.486532 8.75725 0.399414 8.5371 0.399414C8.31694 0.399414 8.10573 0.486532 7.9496 0.641741L4.99959 3.59174L2.04959 0.641741C1.89346 0.486532 1.68225 0.399414 1.46209 0.399414C1.24194 0.399414 1.03073 0.486532 0.874594 0.641741C0.797359 0.719607 0.736256 0.811954 0.694785 0.913485C0.653315 1.01502 0.632294 1.12374 0.632928 1.23341C0.632294 1.34308 0.653315 1.4518 0.694785 1.55333C0.736256 1.65486 0.797359 1.74721 0.874594 1.82507L4.40793 5.35841C4.4854 5.43651 4.57756 5.49851 4.67911 5.54082C4.78066 5.58312 4.88958 5.60491 4.99959 5.60491C5.1096 5.60491 5.21853 5.58312 5.32008 5.54082C5.42162 5.49851 5.51379 5.43651 5.59126 5.35841Z' fill='var(--text-color)' />
              </svg>
            </i>
          </button>
          {/* <div className='setting-dropdwon'>
            <div className='icon-set'>
              <button className='icon-set-btn' onClick={() => setOpenState({ ...openState, isVoiceSettings: !openState['isVoiceSettings'] })}>
                <svg className='gearsvg' width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 15.75C9.93 15.75 8.25 14.07 8.25 12C8.25 9.93 9.93 8.25 12 8.25C14.07 8.25 15.75 9.93 15.75 12C15.75 14.07 14.07 15.75 12 15.75ZM12 9.75C10.76 9.75 9.75 10.76 9.75 12C9.75 13.24 10.76 14.25 12 14.25C13.24 14.25 14.25 13.24 14.25 12C14.25 10.76 13.24 9.75 12 9.75Z" fill="#3A3D44"></path><path d="M15.21 22.19C15 22.19 14.79 22.16 14.58 22.11C13.96 21.94 13.44 21.55 13.11 21L12.99 20.8C12.4 19.78 11.59 19.78 11 20.8L10.89 20.99C10.56 21.55 10.04 21.95 9.42 22.11C8.79 22.28 8.14 22.19 7.59 21.86L5.87 20.87C5.26 20.52 4.82 19.95 4.63 19.26C4.45 18.57 4.54 17.86 4.89 17.25C5.18 16.74 5.26 16.28 5.09 15.99C4.92 15.7 4.49 15.53 3.9 15.53C2.44 15.53 1.25 14.34 1.25 12.88V11.12C1.25 9.66 2.44 8.47 3.9 8.47C4.49 8.47 4.92 8.3 5.09 8.01C5.26 7.72 5.19 7.26 4.89 6.75C4.54 6.14 4.45 5.42 4.63 4.74C4.81 4.05 5.25 3.48 5.87 3.13L7.6 2.14C8.73 1.47 10.22 1.86 10.9 3.01L11.02 3.21C11.61 4.23 12.42 4.23 13.01 3.21L13.12 3.02C13.8 1.86 15.29 1.47 16.43 2.15L18.15 3.14C18.76 3.49 19.2 4.06 19.39 4.75C19.57 5.44 19.48 6.15 19.13 6.76C18.84 7.27 18.76 7.73 18.93 8.02C19.1 8.31 19.53 8.48 20.12 8.48C21.58 8.48 22.77 9.67 22.77 11.13V12.89C22.77 14.35 21.58 15.54 20.12 15.54C19.53 15.54 19.1 15.71 18.93 16C18.76 16.29 18.83 16.75 19.13 17.26C19.48 17.87 19.58 18.59 19.39 19.27C19.21 19.96 18.77 20.53 18.15 20.88L16.42 21.87C16.04 22.08 15.63 22.19 15.21 22.19ZM12 18.49C12.89 18.49 13.72 19.05 14.29 20.04L14.4 20.23C14.52 20.44 14.72 20.59 14.96 20.65C15.2 20.71 15.44 20.68 15.64 20.56L17.37 19.56C17.63 19.41 17.83 19.16 17.91 18.86C17.99 18.56 17.95 18.25 17.8 17.99C17.23 17.01 17.16 16 17.6 15.23C18.04 14.46 18.95 14.02 20.09 14.02C20.73 14.02 21.24 13.51 21.24 12.87V11.11C21.24 10.48 20.73 9.96 20.09 9.96C18.95 9.96 18.04 9.52 17.6 8.75C17.16 7.98 17.23 6.97 17.8 5.99C17.95 5.73 17.99 5.42 17.91 5.12C17.83 4.82 17.64 4.58 17.38 4.42L15.65 3.43C15.22 3.17 14.65 3.32 14.39 3.76L14.28 3.95C13.71 4.94 12.88 5.5 11.99 5.5C11.1 5.5 10.27 4.94 9.7 3.95L9.59 3.75C9.34 3.33 8.78 3.18 8.35 3.43L6.62 4.43C6.36 4.58 6.16 4.83 6.08 5.13C6 5.43 6.04 5.74 6.19 6C6.76 6.98 6.83 7.99 6.39 8.76C5.95 9.53 5.04 9.97 3.9 9.97C3.26 9.97 2.75 10.48 2.75 11.12V12.88C2.75 13.51 3.26 14.03 3.9 14.03C5.04 14.03 5.95 14.47 6.39 15.24C6.83 16.01 6.76 17.02 6.19 18C6.04 18.26 6 18.57 6.08 18.87C6.16 19.17 6.35 19.41 6.61 19.57L8.34 20.56C8.55 20.69 8.8 20.72 9.03 20.66C9.27 20.6 9.47 20.44 9.6 20.23L9.71 20.04C10.28 19.06 11.11 18.49 12 18.49Z" fill="#3A3D44"></path></svg>
              </button>
              {openState['isVoiceSettings'] && voiceSettings()}
            </div>
          </div> */}
        </div>

        {/* ========== Model Selection Dropdown ========== */}
        <div className='gpt-dropdown'>
          <FormControl>
            <Select
              displayEmpty
              open={openState['isModelChange']}
              value={agentData?.['modelType'] || ''}
              onOpen={() => setOpenState({ ...openState, isModelChange: true })}
              onClose={() => setOpenState({ ...openState, isModelChange: false })}
              // onChange={(e) => setAgentData({ ...agentData, modelType: e['target']['value'] })}
              renderValue={(selected) => {
                if (!selected) return 'Select GPT';
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* <i style={{ marginRight: '10px' }}></i> */}
                    <span className='img-box'><img src={agentData['modelId']?.['icon']} alt='' /></span>

                    <Typography sx={{ fontSize: '14px' }}>
                      <p>{agentData['modelId']?.['label']} <span>{`($${agentData['modelId']?.['cost']}/min)`}</span></p>
                    </Typography>
                  </Box>
                );
              }}
              MenuProps={{ disableAutoFocusItem: true }}
            >
              {allModels.length > 0 ? allModels.map(model => (
                <MenuItem key={model['modelName']} value={model['label']} onClick={() => { setAgentData({ ...agentData, modelType: model['modelName'], modelId: model }); setUpdated(true); }}>
                  <span className='img-box'><img src={model['icon']} alt='' /></span>
                  <p>{model['label']} <span>{`($${model['cost']}/min)`}</span></p>
                </MenuItem>
              )) : <MenuItem disabled>No results found</MenuItem>}
            </Select>
          </FormControl>

          <div className='setting-dropdwon'>
            <div className='icon-set'>
              <button className='icon-set-btn' onClick={() => setOpenState({ ...openState, isLLMSettings: !openState['isLLMSettings'] })}>
                <svg className='gearsvg' width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 15.75C9.93 15.75 8.25 14.07 8.25 12C8.25 9.93 9.93 8.25 12 8.25C14.07 8.25 15.75 9.93 15.75 12C15.75 14.07 14.07 15.75 12 15.75ZM12 9.75C10.76 9.75 9.75 10.76 9.75 12C9.75 13.24 10.76 14.25 12 14.25C13.24 14.25 14.25 13.24 14.25 12C14.25 10.76 13.24 9.75 12 9.75Z" fill="#3A3D44"></path><path d="M15.21 22.19C15 22.19 14.79 22.16 14.58 22.11C13.96 21.94 13.44 21.55 13.11 21L12.99 20.8C12.4 19.78 11.59 19.78 11 20.8L10.89 20.99C10.56 21.55 10.04 21.95 9.42 22.11C8.79 22.28 8.14 22.19 7.59 21.86L5.87 20.87C5.26 20.52 4.82 19.95 4.63 19.26C4.45 18.57 4.54 17.86 4.89 17.25C5.18 16.74 5.26 16.28 5.09 15.99C4.92 15.7 4.49 15.53 3.9 15.53C2.44 15.53 1.25 14.34 1.25 12.88V11.12C1.25 9.66 2.44 8.47 3.9 8.47C4.49 8.47 4.92 8.3 5.09 8.01C5.26 7.72 5.19 7.26 4.89 6.75C4.54 6.14 4.45 5.42 4.63 4.74C4.81 4.05 5.25 3.48 5.87 3.13L7.6 2.14C8.73 1.47 10.22 1.86 10.9 3.01L11.02 3.21C11.61 4.23 12.42 4.23 13.01 3.21L13.12 3.02C13.8 1.86 15.29 1.47 16.43 2.15L18.15 3.14C18.76 3.49 19.2 4.06 19.39 4.75C19.57 5.44 19.48 6.15 19.13 6.76C18.84 7.27 18.76 7.73 18.93 8.02C19.1 8.31 19.53 8.48 20.12 8.48C21.58 8.48 22.77 9.67 22.77 11.13V12.89C22.77 14.35 21.58 15.54 20.12 15.54C19.53 15.54 19.1 15.71 18.93 16C18.76 16.29 18.83 16.75 19.13 17.26C19.48 17.87 19.58 18.59 19.39 19.27C19.21 19.96 18.77 20.53 18.15 20.88L16.42 21.87C16.04 22.08 15.63 22.19 15.21 22.19ZM12 18.49C12.89 18.49 13.72 19.05 14.29 20.04L14.4 20.23C14.52 20.44 14.72 20.59 14.96 20.65C15.2 20.71 15.44 20.68 15.64 20.56L17.37 19.56C17.63 19.41 17.83 19.16 17.91 18.86C17.99 18.56 17.95 18.25 17.8 17.99C17.23 17.01 17.16 16 17.6 15.23C18.04 14.46 18.95 14.02 20.09 14.02C20.73 14.02 21.24 13.51 21.24 12.87V11.11C21.24 10.48 20.73 9.96 20.09 9.96C18.95 9.96 18.04 9.52 17.6 8.75C17.16 7.98 17.23 6.97 17.8 5.99C17.95 5.73 17.99 5.42 17.91 5.12C17.83 4.82 17.64 4.58 17.38 4.42L15.65 3.43C15.22 3.17 14.65 3.32 14.39 3.76L14.28 3.95C13.71 4.94 12.88 5.5 11.99 5.5C11.1 5.5 10.27 4.94 9.7 3.95L9.59 3.75C9.34 3.33 8.78 3.18 8.35 3.43L6.62 4.43C6.36 4.58 6.16 4.83 6.08 5.13C6 5.43 6.04 5.74 6.19 6C6.76 6.98 6.83 7.99 6.39 8.76C5.95 9.53 5.04 9.97 3.9 9.97C3.26 9.97 2.75 10.48 2.75 11.12V12.88C2.75 13.51 3.26 14.03 3.9 14.03C5.04 14.03 5.95 14.47 6.39 15.24C6.83 16.01 6.76 17.02 6.19 18C6.04 18.26 6 18.57 6.08 18.87C6.16 19.17 6.35 19.41 6.61 19.57L8.34 20.56C8.55 20.69 8.8 20.72 9.03 20.66C9.27 20.6 9.47 20.44 9.6 20.23L9.71 20.04C10.28 19.06 11.11 18.49 12 18.49Z" fill="#3A3D44"></path></svg>
              </button>
              {openState['isLLMSettings'] && llmSettings()}
            </div>
          </div>
        </div>

        {/* ========== Country Selection Autocomplete ========== */}
        <div className='gpt-dropdown style-two'>
          <Autocomplete
            autoHighlight
            name='language'
            sx={{ width: 155, fontSize: '12px' }}
            id='country-select-demo'
            options={constants['languagesList']}
            value={agentData?.['language'] || {}}
            getOptionLabel={(option) => option['label'] || 'Select language'}
            onChange={(e, language) => { setAgentData({ ...agentData, language }); setUpdated(true); }}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <Box key={key} component='li' sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...optionProps}>
                  <img loading='lazy' width='20' srcSet={`https://flagcdn.com/w40/${option['icon'].toLowerCase()}.png 2x`} src={`https://flagcdn.com/w20/${option['icon'].toLowerCase()}.png`} alt='' style={{ marginRight: '10px' }} />
                  {option['label']}
                </Box>
              );
            }}
            renderInput={(params) => (<TextField {...params} placeholder="Select language" InputLabelProps={{ shrink: false }} slotProps={{ htmlInput: { ...params['inputProps'], autoComplete: 'new-password' } }} />)}
          />
        </div>
      </div>

      {/* --------------- Agents Voice Selection Modal--------------- */}
      <Modal isOpen={openState['isVoiceChange']} className={`main-modal new-user-modal modal-dialog-centered voice-change-modal select-tampltes-modal`}>
        <ModalHeader toggle={() => setOpenState({ ...openState, isVoiceChange: false })}></ModalHeader>
        <ModalBody>
          <div className='agent-modal-area'>
            <h3>Select Voice</h3>
            <div className='bottom-content-area'>
              <div className='tabs-content'>
                <div className='tabs-email'>
                  <FormControl fullWidth variant='outlined' size='small'>
                    <Select value={voiceProvider} onChange={(e) => setVoiceProvider(e['target']['value'])} displayEmpty>
                      {ttsProviders?.map((provider) => (
                        <MenuItem key={provider.provider} value={provider.provider}>
                          {provider.label || provider.provider}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                <TabPanel>
                  <div className='agent-voice-select'>
                    <div className='agent-voice-inner'>
                      <div className='agent-voice-top'>
                        {/* Search Field */}
                        <div className='group-form search-feild'>
                          <TextField
                            fullWidth
                            hiddenLabel
                            size='small'
                            variant='standard'
                            value={searchVoice}
                            placeholder='Search'
                            onChange={(e) => setSearchVoice(e['target']['value'])}
                          />
                        </div>
                        {/* Gender Filter */}
                        <div className='group-form'>
                          <FormControl fullWidth>
                            <Select
                              displayEmpty
                              value={selectedGender}
                              open={openState['gender']}
                              renderValue={(selected) => selected || 'Gender'}
                              onChange={(e) => handleSelectGender(e['target']['value'])}
                              onOpen={() => setOpenState({ ...openState, gender: true })}
                              onClose={() => setOpenState({ ...openState, gender: false })}
                            >
                              {dynamicFilters['genders'].map((gender) => (
                                <MenuItem key={gender} value={gender} onClick={() => handleSelectGender(gender)}>
                                  {gender}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </div>

                        {/* Accent Filter */}
                        <div className='group-form'>
                          <FormControl fullWidth>
                            <Select
                              displayEmpty
                              value={selectedAccent}
                              open={openState['accent']}
                              onOpen={() => setOpenState({ ...openState, accent: true })}
                              onClose={() => setOpenState({ ...openState, accent: false })}
                              renderValue={(selected) => selected || 'Accent'}
                              onChange={(e) => handleSelectAccent(e['target']['value'])}
                            >
                              {dynamicFilters['accents'].map((accent) => (
                                <MenuItem key={accent} value={accent} onClick={() => handleSelectAccent(accent)}> {accent} </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </div>
                      </div>

                      {/* Voices Table */}
                      <div className='voice-table-area'>
                        <div className='table-container'>
                          <ReactTable
                            width='100'
                            minRows={6}
                            filterable={false}
                            data={filteredVoices.map((voice) => ({
                              voiceName: (
                                <div className='voice-box'>
                                  <button className='ply-btn' onClick={() => playVoiceSample(voice['demoUrl'], voice['voiceName'])}>
                                    {currentPlayingVoice === voice['voiceName'] ? (
                                      <svg width='24' height='25' viewBox='0 0 24 25' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                        <rect x='6' y='5' width='4' height='15' fill='#242424' />
                                        <rect x='14' y='5' width='4' height='15' fill='#242424' />
                                      </svg>
                                    ) : (
                                      <svg width='24' height='25' viewBox='0 0 24 25' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                        <path d='M4.78271 6.11148C4.78271 5.1003 5.91153 4.49889 6.75078 5.06293L16.6139 11.6917C17.3587 12.1923 17.3587 13.2882 16.6139 13.7888L6.75078 20.4175C5.91153 20.9816 4.78271 20.3802 4.78271 19.369V6.11148Z' fill='#242424' />
                                      </svg>
                                    )}
                                  </button>
                                  <span className='img-box'> <img src={voice['pictureUrl']} /> </span>
                                  {voice['voiceName']}
                                </div>
                              ),
                              voiceTrait: (
                                <div className='trait-box'>
                                  <button className='trait-btn'>{voice['accent']}</button>
                                  <button className='trait-btn'>{voice['gender']}</button>
                                </div>
                              ),
                              voiceKey: voice['voiceKey'],
                              actionsAgent: (<button className='btn-style-new' onClick={() => { dispatch(updateAgentVoice({ ...voice, id })); setOpenState({ ...openState, isVoiceChange: false }) }}>Use Voice</button>)
                            }))}
                            showPagination={false}
                            columns={[
                              { id: 'voiceName', Header: 'Voice', accessor: 'voiceName' },
                              { id: 'voiceTrait', Header: 'Trait', accessor: 'voiceTrait' },
                              { id: 'voiceKey', Header: 'Voice ID', accessor: 'voiceKey' },
                              { id: 'actionsAgent', Header: '', accessor: 'actionsAgent' }
                            ]}
                            className='table campaign-table responsive contacts-table'
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </TabPanel>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal >

      {/* --------------- Agents Cutom Voice Selection Modal --------------- */}
      <Modal isOpen={valueCustomVoiceTab} className={`main-modal new-user-modal modal-dialog-centered voice-change-modal select-tampltes-modal`}>
        <ModalHeader toggle={() => setValueCustomVoiceTab(!valueCustomVoiceTab)}></ModalHeader>
        <ModalBody>
          <div className='agent-modal-area'>
            <h3>Add Custom Voice</h3>
            <div className='bottom-content-area style-custom-voice'>
              <div className='tabs-box'>
                <div className='tabs-email'>
                  <AppBar position='static'>
                    <Tabs value={valueCustomVoiceTab} onChange={handleTabsSetCustomVoice} aria-label='simple tabs example'>
                      <Tab label='Voice Clone' />
                      <Tab label='Community Voices' />
                    </Tabs>
                  </AppBar>
                </div>
              </div>

              <div className='tabs-content'>
                <TabPanel value={valueCustomVoiceTab} index={0}>
                  <div className='agent-voice-select'>
                    <div className='agent-voice-inner'>
                      <div className='agent-voice-top'>
                        <div className='group-form search-feild'>
                          <p>Voice Name</p>
                          <TextField
                            fullWidth
                            name='sec'
                            hiddenLabel
                            size='small'
                            variant='standard'
                            placeholder='Enter a voice name'
                          // onChange={handleEditChange}
                          // value={editState?.type || agentData?.type || ''}
                          />
                        </div>

                        <div className='group-form upload-feild'>
                          <p>Upload audio clip</p>
                          <div className='flex flex-col items-center p-6 border-2 border-dashed border-gray-300 rounded-lg'>
                            <div {...getRootProps({ className: 'cursor-pointer text-center' })}>
                              <input {...getInputProps()} />
                              <div className='text-blue-500 text-3xl'>
                                <svg width='26' height='26' viewBox='0 0 26 26' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                  <path d='M8.86514 7.46953L11.7203 4.60186V16.5588C11.7203 16.8895 11.8517 17.2066 12.0855 17.4404C12.3194 17.6743 12.6365 17.8056 12.9672 17.8056C13.2978 17.8056 13.615 17.6743 13.8488 17.4404C14.0826 17.2066 14.214 16.8895 14.214 16.5588V4.60186L17.0692 7.46953C17.1851 7.58639 17.323 7.67915 17.4749 7.74245C17.6269 7.80575 17.7898 7.83834 17.9544 7.83834C18.119 7.83834 18.282 7.80575 18.4339 7.74245C18.5858 7.67915 18.7237 7.58639 18.8396 7.46953C18.9565 7.35363 19.0493 7.21573 19.1126 7.06379C19.1759 6.91186 19.2085 6.74889 19.2085 6.5843C19.2085 6.4197 19.1759 6.25674 19.1126 6.1048C19.0493 5.95286 18.9565 5.81497 18.8396 5.69906L13.8524 0.711804C13.7338 0.598293 13.594 0.509314 13.4409 0.449973C13.1374 0.325269 12.7969 0.325269 12.4934 0.449973C12.3403 0.509314 12.2005 0.598293 12.0819 0.711804L7.09467 5.69906C6.97842 5.81531 6.8862 5.95332 6.82329 6.10521C6.76037 6.2571 6.72799 6.41989 6.72799 6.5843C6.72799 6.7487 6.76037 6.91149 6.82329 7.06338C6.8862 7.21527 6.97842 7.35328 7.09467 7.46953C7.21092 7.58578 7.34893 7.678 7.50082 7.74091C7.65271 7.80383 7.8155 7.83621 7.9799 7.83621C8.14431 7.83621 8.3071 7.80383 8.45899 7.74091C8.61088 7.678 8.74889 7.58578 8.86514 7.46953ZM24.1885 12.8184C23.8578 12.8184 23.5407 12.9497 23.3068 13.1835C23.073 13.4174 22.9417 13.7345 22.9417 14.0652V21.5461C22.9417 21.8767 22.8103 22.1939 22.5765 22.4277C22.3427 22.6615 22.0255 22.7929 21.6949 22.7929H4.23946C3.90879 22.7929 3.59166 22.6615 3.35783 22.4277C3.12401 22.1939 2.99265 21.8767 2.99265 21.5461V14.0652C2.99265 13.7345 2.86129 13.4174 2.62747 13.1835C2.39364 12.9497 2.07651 12.8184 1.74584 12.8184C1.41516 12.8184 1.09803 12.9497 0.864207 13.1835C0.630384 13.4174 0.499023 13.7345 0.499023 14.0652V21.5461C0.499023 22.5381 0.893105 23.4895 1.59457 24.1909C2.29604 24.8924 3.24744 25.2865 4.23946 25.2865H21.6949C22.6869 25.2865 23.6383 24.8924 24.3397 24.1909C25.0412 23.4895 25.4353 22.5381 25.4353 21.5461V14.0652C25.4353 13.7345 25.3039 13.4174 25.0701 13.1835C24.8363 12.9497 24.5192 12.8184 24.1885 12.8184Z' fill='#4C9EF7' />
                                </svg>
                              </div>
                              <h4 className='mt-2'>Choose a file or drag & drop it here</h4>
                              <p className='text-sm text-gray-500'>Audio and video formats, up to 10 MB.</p>
                            </div>
                            {file && (
                              <div className='mt-4 p-2 border border-gray-300 rounded'>
                                <p>Uploaded: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className='group-form checkbox-feild'>
                          <div className='box-check'>
                            <label>
                              <Checkbox />
                              <p>I hereby confirm that I have all necessary rights or consents to upload and clone these voice samples and that I will not use the platform-generated content for any illegal, fraudulent, or harmful purpose.</p>
                            </label>
                          </div>
                          <div className='btn-groups'>
                            <button className='btn-style-border'>Cancel</button>
                            <button className='btn-style-new'>Save</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabPanel>

                <TabPanel value={valueCustomVoiceTab} index={1}>
                  <div className='agent-voice-select'>
                    <div className='agent-voice-select'>
                      <div className='agent-voice-inner'>
                        <div className='agent-voice-top'>
                          <div className='group-form search-feild'>
                            <p>Voice Name</p>
                            <p className='link-prhagrap'>Explore the community voices <a href='#'> here</a></p>
                            <TextField
                              fullWidth
                              name='sec'
                              hiddenLabel
                              size='small'
                              variant='standard'
                              placeholder='Search by voice name'
                            // onChange={handleEditChange}
                            // value={editState?.type || agentData?.type || ''}
                            />
                          </div>
                          <div className='group-form checkbox-feild mt-4'>
                            <div className='btn-groups'>
                              <button className='btn-style-border'>Cancel</button>
                              <button className='btn-style-new'>Save</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabPanel>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  )
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role='tabpanel' hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other} >
      {value === index && (<Box p={3}> <Typography>{children}</Typography> </Box>)}
    </div>
  );
}

TabPanel.propTypes = { children: PropTypes.node, index: PropTypes.any['isRequired'], value: PropTypes.any['isRequired'] };
const optionsGender = constants['voiceOptions']['gender'], optionsAccent = constants['voiceOptions']['accent'], optionsTypes = constants['voiceOptions']['types'];

export default TopSettings;